import { Component, DestroyRef, inject, InjectionToken } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AnalyticsService } from '@app/core/analytics/analytics.service';
import { AppConfig } from '@app/core/app.config';
import { DatagridVisualisationEnum } from '@app/core/enums/datagrid-visualisation.enum';
import { ColumnBuilder } from '@app/shared/grid/column-builder';
import { ExportUtils } from '@app/shared/grid/export-utils';
import { GridOptionsService } from '@app/shared/grid/grid-options.service';
import { GridStateService } from '@app/shared/services/grid-state.service';
import { TranslateService } from '@ngx-translate/core';
import { GridConfigService } from '@services/grid-config.service';
import { AccessManager } from '@services/managers/access.manager';
import { AppManager } from '@services/managers/app.manager';
import { SnackbarManager } from '@services/managers/snackbar.manager';
import {
  ColGroupDef,
  GridApi,
  GridOptions,
  GridReadyEvent,
  IColumnToolPanel,
  IToolPanelColumnCompParams,
  ProcessCellForExportParams,
  SELECTION_COLUMN_ID,
  ToolPanelDef
} from 'ag-grid-community';
import { DeviceDetectorService } from 'ngx-device-detector';
import { fromEventPattern } from 'rxjs';

export const DATAGRID = new InjectionToken<string>('datagrid');

// TODO TTT-3026 Remove and use datagrid
export type VisualisationKey = 'assetDatagridVisualisationId' |
  'organizationDocumentsDatagridVisualisationId' |
  'organizationSpacesDatagridVisualisationId' |
  'organizationChecksDatagridVisualisationId' |
  'organizationContractsDatagridVisualisationId' |
  'organizationOccupantsDatagridVisualisationId' |
  'assetChecksDatagridVisualisationId' |
  'assetOccupantsDatagridVisualisationId' |
  'assetContractsDatagridVisualisationId' |
  'assetDocumentsDatagridVisualisationId' |
  'assetSpacesDatagridVisualisationId' |
  'assetWorksDatagridVisualisationId' |
  'projectWorksDatagridVisualisationId' |
  'worksDatagridVisualisationId' |
  'assetEquipmentsDatagridVisualisationId' |
  'equipmentWorksDatagridVisualisationId' |
  'equipmentsDatagridVisualisationId' |
  'projectsDatagridVisualisationId' |
  'assetProjectsDatagridVisualisationId' |
  'projectDocumentsDatagridVisualisationId' |
  'investmentsDatagridVisualisationId' |
  'assetInvestmentsDatagridVisualisationId' |
  'projectInvestmentsDatagridVisualisationId' |
  'equipmentInvestmentsDatagridVisualisationId' |
  'workInvestmentsDatagridVisualisationId' |
  'investmentDocumentsDatagridVisualisationId' |
  'workDocumentsDatagridVisualisationId' |
  'equipmentDocumentsDatagridVisualisationId' |
  'meetingsDatagridVisualisationId' |
  'meetingWorksDatagridVisualisationId' |
  'equipmentEquipmentsDatagridVisualisationId' |
  'assetGroupDatagridVisualisationId' |
  'assetGroupAssetsDatagridVisualisationId' |
  'assetGroupDocumentsDatagridVisualisationId';

// TODO TTT-3025 Move duplicated code from datagrids in this class
@Component({template: '', standalone: false})
export abstract class AbstractDatagrid {
  protected abstract datagrid: DatagridVisualisationEnum;

  protected gridApi: GridApi;
  protected gridOptions: GridOptions;
  protected toolPanelLayout: ColGroupDef[];
  protected assetTypeNameColId = 'assetTypeName';
  protected selectedRowsCount = 0;
  protected isExportInProgress = false;

  protected gridStateService = inject(GridStateService);
  protected accessManager = inject(AccessManager);
  protected appManager = inject(AppManager);
  protected appConfig = inject(AppConfig);
  protected gridOptionsService = inject(GridOptionsService);
  protected exportUtils = inject(ExportUtils);
  protected deviceDetectorService = inject(DeviceDetectorService);
  protected translate: TranslateService = inject(TranslateService);
  protected analyticsService = inject(AnalyticsService);
  protected gridConfigService = inject(GridConfigService);
  protected columnBuilder = inject(ColumnBuilder);
  protected snackbarManager = inject(SnackbarManager);
  protected destroyRef = inject(DestroyRef);

  /**
   * Stop listening to Observables.
   */
  public onDestroy(): void {
    this.gridStateService.onGridDestroyed();
  }

  /**
   * Save grid and column APIs after AG-Grid's component is ready.
   * @param params AG-Grid's params containing the APIs.
   */
  public onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
    this.gridStateService.sessionStorageKey = this.gridStateId;

    // Trigger destroy when the grid is destroyed
    fromEventPattern(
      handler => this.gridApi.addEventListener('gridPreDestroyed', handler),
      handler => this.gridApi.removeEventListener('gridPreDestroyed', handler)
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();

    // Listen for new grid states to apply from the GridStateService
    this.gridStateService.restoreGridState$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(gridState => {
        this.gridStateService.lockGridState();

        // Update column states, apply default hidden/unsorted state to all other columns
        this.gridApi.applyColumnState({
          state: [
            ...(gridState.columnState ?? []),
            // The row selection checkbox column is generated internally by AG Grid and never appears in the
            // persisted state, so it must be kept visible explicitly to avoid falling under defaultState.hide.
            {colId: SELECTION_COLUMN_ID, hide: false}
          ],
          applyOrder: true,
          defaultState: {
            hide: true,
            sort: null,
            sortIndex: null,
            rowGroup: false
          }
        });

        // Update filters
        this.gridApi.setFilterModel(gridState.filterModel ?? {});
        this.gridApi.onFilterChanged();

        // Resume listening to grid state updates
        // setTimeout is necessary to prevent asynchronous Ag-grid events raised while restoring the state to change the saved grid state
        setTimeout(() => this.gridStateService.unlockGridState(), 0);
      });
  }

  /**
   * Returns the default tool panels to which the visualisation tool panel is added.
   */
  protected get extendedToolPanels(): ToolPanelDef[] {
    return [
      ...AbstractDatagrid.defaultToolPanels,
      {
        id: 'visualisation',
        labelDefault: 'Visualisation',
        labelKey: 'visualisation',
        iconKey: 'eye',
        toolPanel: 'visualisationToolPanel',
        toolPanelParams: {
          preferenceIdKey: this.preferredVisualisationIdKey,
          assetTypeNameColId: this.assetTypeNameColId
        }
      }
    ];
  }

  /**
   * Save the column groups definitions that are used in the tool panel, so that they can be reapplied after toggling
   * the edit mode.
   * @param colGroupDefs Tool panel layout.
   */
  public saveToolPanel(colGroupDefs: ColGroupDef[]): void {
    this.toolPanelLayout = colGroupDefs;
  }

  /**
   * Apply the saved tool panel layout.
   * @param id ID of the tool panel.
   * @param gridApi API of the grid on which to update the tool panel's layout.
   */
  public restoreToolPanel(id: string, gridApi: GridApi): void {
    if (this.toolPanelLayout) {
      const columnToolPanel = gridApi.getToolPanelInstance<IColumnToolPanel>(id);
      columnToolPanel.setColumnLayout(this.toolPanelLayout);
      columnToolPanel.collapseColumnGroups();
    }
  }

  /**
   * ID to be used for a GridState used by the current user within the current Organization.
   * This ID can then be set as the GridStateService's session storage key.
   * @protected
   */
  protected get gridStateId(): string {
    return `grid-state-${this.datagrid}-${this.accessManager.currentUser.id}-${this.appManager.currentOrganization.id}`;
  }

  /**
   * Returns the base tool panels to use for all datagrids.
   * @protected
   */
  protected static get defaultToolPanels(): ToolPanelDef[] {
    return [
      {
        id: 'columns',
        labelDefault: 'Columns',
        labelKey: 'columns',
        iconKey: 'columns',
        toolPanel: 'agColumnsToolPanel',
        toolPanelParams: <IToolPanelColumnCompParams>{
          suppressColumnMove: true,
          suppressSyncLayoutWithGrid: true,
          suppressColumnExpandAll: true,
          suppressPivots: true,
          suppressPivotMode: true,
          suppressValues: true,
          suppressRowGroups: true
        }
      },
      {
        id: 'filters',
        labelDefault: 'Filters',
        labelKey: 'filters',
        iconKey: 'filter',
        toolPanel: 'agFiltersToolPanel'
      }
    ];
  }

  /**
   * Format each cell of a grid before exporting data as CSV to avoid a security issue.
   * Prevent a value to begin with symbols which could lead to an automatic execution by spreadsheet
   * applications like Excel, Apple Numbers or Google Sheets.
   *
   * @param params Parameters provided by AG Grid containing the cell value and context.
   * @return The sanitized cell value as a string, safe for CSV export.
   * @protected
   */
  protected processCellsForCSVExport(params: ProcessCellForExportParams): string {
    const cellValue = params.value?.toString() ?? '';
    return cellValue.length > 0 && this.appConfig.EXPORT_CELLS_FORBIDDEN_VALUES.includes(cellValue.at(0)) ?
      `\\${cellValue}` : params.value;
  }

  /**
   * Setup gridOptions with default and extra settings
   * @param extraGridOptions Class specific settings to add to grid
   * @protected
   */
  protected initGridOptions(extraGridOptions: GridOptions): void {
    this.gridOptions = {
      ...this.gridOptionsService.staticGridOptions,
      onGridPreDestroyed: () => this.gridApi.stopEditing(true),
      ...extraGridOptions,
    };
  }

  // TODO TTT-3026 Remove and use datagrid
  /**
   * Retrieves the property key of the related visualisation that is stored in user preferences.
   * @returns {VisualisationKey | null} The property key or null.
   * @protected
   */
  protected abstract get preferredVisualisationIdKey(): VisualisationKey | null;
}
